import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import ImageUploader from '../components/ImageUploader';

const SevaOffering = () => {
  const [showForm, setShowForm] = useState(false);
  const [sevaOfferingItems, setSevaOfferingItems] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [centreFilter, setCentreFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');

  const [formData, setFormData] = useState({
    name: '',
    price: '',
    weight: '',
    category: '',
    image: '',
    centreId: '',
    vendorId: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sevaOfferingRes, centresRes, vendorsRes] = await Promise.all([
        api.get('/seva-offerings'),
        api.get('/centres'),
        api.get('/admin/vendors')
      ]);
      setSevaOfferingItems(sevaOfferingRes.data.data || sevaOfferingRes.data || []);
      setCentres(centresRes.data.data || centresRes.data || []);
      setVendors(vendorsRes.data.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setLoading(false);
    }
  };

  const filteredSevaOffering = sevaOfferingItems
    .filter(p => {
      const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCentre = centreFilter === 'all' || p.centreId?._id === centreFilter || p.centreId === centreFilter;
      return matchesSearch && matchesCentre;
    })
    .sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
      if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      return 0;
    });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };



  const handleSave = async () => {
    if (!formData.name || !formData.price || !formData.centreId) {
      alert("Name, Price, and Centre are required.");
      return;
    }

    try {
      if (editMode) {
        await api.put(`/seva-offerings/${selectedId}`, formData);
        alert("✅ SevaOffering updated successfully!");
      } else {
        await api.post(`/seva-offerings`, formData);
        alert("✅ New SevaOffering added!");
      }
      fetchData();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("❌ Error saving sevaOffering.");
    }
  };

  const handleEdit = (item) => {
    setEditMode(true);
    setSelectedId(item._id);
    setFormData({
      name: item.name,
      price: item.price,
      weight: item.weight || '',
      category: item.category || '',
      image: item.image || '',
      centreId: item.centreId?._id || item.centreId || '',
      vendorId: item.vendorId?._id || item.vendorId || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sevaOffering?")) return;
    try {
      await api.delete(`/seva-offerings/${id}`);
      alert("✅ SevaOffering deleted!");
      fetchData();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setSelectedId(null);
    setFormData({
      name: '',
      price: '',
      weight: '',
      category: '',
      image: '',
      centreId: '',
      vendorId: ''
    });
  };

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2A2858]">SevaOffering Inventory</h1>
          <p className="text-stone-500 mt-1 text-sm">Manage SevaOffering offerings, prices, and photos</p>
        </div>
        
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-48">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">search</span>
            <input 
              type="text" 
              placeholder="Search SevaOffering..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B]"
            />
          </div>
          
          <select 
            value={centreFilter}
            onChange={(e) => setCentreFilter(e.target.value)}
            className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
          >
            <option value="all">All Centres</option>
            {centres.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>

          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white border border-stone-200 rounded-xl text-sm outline-none focus:border-[#E8B84B] text-stone-600 font-bold"
          >
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="price_asc">Price (Low-High)</option>
            <option value="price_desc">Price (High-Low)</option>
          </select>

          <button
            onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="bg-[#2A2858] hover:bg-[#600018] text-white px-5 py-2 rounded-xl font-bold text-sm shadow-lg transition-colors flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">
              {showForm ? 'close' : 'add'}
            </span>
            {showForm ? 'Cancel' : 'Add SevaOffering'}
          </button>
        </div>
      </div>

      {/* Add New SevaOffering Form */}
      {showForm && (
        <div className="bg-[#0E0E26] p-8 rounded-2xl mb-8 border border-[#E8B84B]/30 shadow-2xl transition-all">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">{editMode ? 'Edit SevaOffering' : 'Add New SevaOffering'}</h2>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-red-400 hover:text-red-300 text-sm font-bold">Cancel</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Item Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. Besan Laddoo" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Price (₹)</label>
              <input type="number" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. 50" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Weight</label>
              <input type="text" name="weight" value={formData.weight} onChange={handleInputChange} placeholder="e.g. 250g or 1 Box" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Category</label>
              <input type="text" name="category" value={formData.category} onChange={handleInputChange} placeholder="General Offerings" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-4">SevaOffering Image (Cloudinary)</label>
              <div className="flex flex-col md:flex-row gap-6 items-start bg-[#14122B] p-6 rounded-2xl border border-stone-700">
                {formData.image ? (
                  <div className="w-full md:w-48 aspect-square rounded-xl overflow-hidden border-2 border-[#E8B84B]/30 shadow-lg relative group">
                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-[8px] font-black uppercase tracking-widest">Current SevaOffering Photo</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-full md:w-48 aspect-square rounded-xl border-2 border-dashed border-stone-700 flex flex-col items-center justify-center bg-[#0E0E26]">
                    <span className="material-symbols-outlined text-stone-700 text-4xl mb-2">inventory_2</span>
                    <span className="text-[9px] text-stone-600 font-black uppercase tracking-widest text-center">No Image<br/>Selected</span>
                  </div>
                )}
                
                <div className="flex-1 space-y-4">
                  <ImageUploader 
                    buttonText={formData.image ? "Change SevaOffering Image" : "Upload SevaOffering Image"}
                    onUploadSuccess={(url) => setFormData({...formData, image: url})}
                    folder="sevaOffering"
                  />
                  <div className="space-y-1">
                    <p className="text-[10px] text-stone-500 font-medium italic flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">cloud_upload</span>
                      Directly optimized by Cloudinary CDN
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Assign Centre</label>
              <select name="centreId" value={formData.centreId} onChange={handleInputChange} className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] appearance-none cursor-pointer">
                <option value="">Select Centre</option>
                {centres.map(t => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Assign SevaOffering Vendor</label>
              <div className="relative">
                <select 
                  name="vendorId" 
                  value={formData.vendorId} 
                  onChange={handleInputChange} 
                  className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] appearance-none cursor-pointer pr-10"
                >
                  <option value="">-- No Vendor (Self Managed) --</option>
                  {vendors.map(v => (
                    <option key={v._id} value={v._id}>{v.userId?.name || v.name} ({v.type || 'Vendor'})</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-stone-500">
                  <span className="material-symbols-outlined text-[18px]">expand_more</span>
                </div>
              </div>
              <p className="text-[9px] text-stone-600 mt-1 italic">This vendor will be responsible for fulfilling orders of this SevaOffering item.</p>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button onClick={handleSave} className="bg-[#E8B84B] hover:bg-[#b5952f] text-black px-8 py-2.5 rounded-lg font-bold transition-colors">
              {editMode ? 'Update SevaOffering' : 'Save SevaOffering'}
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-stone-500 font-bold animate-pulse">Loading SevaOffering...</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100 text-[10px] uppercase tracking-widest text-[#2A2858] font-bold">
                <th className="p-4 w-16">Image</th>
                <th className="p-4">Name</th>
                <th className="p-4">Centre</th>
                <th className="p-4">Category</th>
                <th className="p-4">Price</th>
                <th className="p-4">Weight</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSevaOffering.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-10 text-center text-stone-500 font-medium">
                    No matching sevaOffering items found.
                  </td>
                </tr>
              ) : (
                filteredSevaOffering.map(item => (
                  <tr key={item._id} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="p-4">
                      <div className="w-10 h-10 rounded bg-stone-100 flex justify-center items-center overflow-hidden">
                        {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-stone-300">inventory_2</span>}
                      </div>
                    </td>
                    <td className="p-4 font-bold text-stone-800">{item.name}</td>
                    <td className="p-4 text-stone-600 text-sm">{item.centreId?.name || 'N/A'}</td>
                    <td className="p-4 text-stone-600 text-sm">{item.category}</td>
                    <td className="p-4 text-[#2A2858] font-bold">₹{item.price}</td>
                    <td className="p-4 text-stone-600 text-sm">{item.weight}</td>
                    <td className="p-4">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => handleEdit(item)} className="text-stone-400 hover:text-blue-500 transition-colors">
                          <span className="material-symbols-outlined text-xl">edit</span>
                        </button>
                        <button onClick={() => handleDelete(item._id)} className="text-stone-400 hover:text-red-500 transition-colors">
                          <span className="material-symbols-outlined text-xl">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SevaOffering;